import {
  computed,
  ref,
  toValue,
  watchEffect,
  type MaybeRefOrGetter,
} from "vue";

export interface DumbStorageSerializer<T> {
  read: (value: string) => T;
  write: (value: T) => string | null;
}

export interface DumbStorageOptions<T> {
  serializer: DumbStorageSerializer<T>;
  storage?: Storage;
}

export function useDumbStorage<T>(
  key: MaybeRefOrGetter<string>,
  defaultValue: MaybeRefOrGetter<T>,
  options: DumbStorageOptions<T>,
) {
  const storage = options.storage ?? localStorage;
  const persistentValue = ref(storage.getItem(toValue(key)));

  function dispatchWriteEvent(
    oldValue: string | null,
    newValue: string | null,
  ) {
    // send custom event to communicate within same page
    if (window) {
      const payload = {
        key: toValue(key),
        oldValue,
        newValue,
        storageArea: storage as Storage,
      };
      // We also use a CustomEvent since StorageEvent cannot
      // be constructed with a non-built-in storage area
      window.dispatchEvent(new StorageEvent("storage", payload));
    }
  }

  const currentValue = computed({
    get() {
      return persistentValue.value === null
        ? toValue(defaultValue)
        : options.serializer.read(persistentValue.value);
    },
    set(value: T) {
      const serialized = options.serializer.write(value);

      if (serialized === null) {
        storage.removeItem(toValue(key));
      } else {
        storage.setItem(toValue(key), serialized);
      }

      dispatchWriteEvent(persistentValue.value, serialized);
    },
  });

  watchEffect((onCleanup) => {
    const keyValue = toValue(key);

    function listener(event: StorageEvent) {
      if (event.key === keyValue) {
        persistentValue.value = event.newValue;
      }
    }

    persistentValue.value = storage.getItem(keyValue);

    window.addEventListener("storage", listener);
    onCleanup(() => {
      window.removeEventListener("storage", listener);
    });
  });

  return currentValue;
}

export interface DumbStorageValueConfig<T> {
  key: string;
  defaultValue: T;
  options: DumbStorageOptions<T>;
  storage?: Storage;
}

export class DumbStorageValue<T> {
  value: T | null = null;
  protected key: string;
  protected defaultValue: T;
  protected options: DumbStorageOptions<T>;
  protected storage: Storage;

  constructor({
    key,
    defaultValue,
    options,
    storage = localStorage,
  }: DumbStorageValueConfig<T>) {
    this.storage = storage;

    this.key = key;
    this.value = this.storage.getItem(key)
      ? options.serializer.read(this.storage.getItem(key)!)
      : null;
    this.defaultValue = defaultValue;
    this.options = options;
  }

  get(): T {
    if (this.value === null) {
      return this.defaultValue;
    }

    return this.value;
  }

  set(value: T) {
    const serialized = this.options.serializer.write(value);
    if (serialized === null) {
      this.storage.removeItem(this.key);
    } else {
      this.storage.setItem(this.key, serialized);
    }
    this.value = value;
  }

  remove() {
    this.storage.removeItem(this.key);
    this.value = null;
  }
}
